import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList } from 'recharts';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const FinancialChart = () => {
  const { t } = useTranslation();
  const { stats } = useSelector((state) => state.dashboard);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeBar, setActiveBar] = useState('all');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const financialData = stats?.financialStats || [];

  const transformedData = financialData
    .map(item => ({
      name: item.month,
      Collected: item.collected,
      Pending: item.pending,
      Overdue: item.overdue,
      Late: item.late,
      Total: item.collected + item.pending + item.overdue + item.late
    }))
    .filter(item => item.Total > 0);
  
  // Get the total amount for the summary
  const totalCollected = transformedData.reduce((sum, item) => sum + item.Collected, 0);
  const totalPending = transformedData.reduce((sum, item) => sum + item.Pending, 0);
  const totalOverdue = transformedData.reduce((sum, item) => sum + item.Overdue, 0);
  const totalLate = transformedData.reduce((sum, item) => sum + item.Late, 0);
  const grandTotal = totalCollected + totalPending + totalOverdue + totalLate;

  // Calculate growth percentage
  const calculateGrowthTrend = () => {
    if (transformedData.length < 2) return 0;
    
    const firstMonth = transformedData[0].Collected;
    const lastMonth = transformedData[transformedData.length - 1].Collected;
    
    if (firstMonth === 0) return 0;
    return ((lastMonth - firstMonth) / firstMonth * 100).toFixed(1);
  };

  const growthPercentage = calculateGrowthTrend();

  const handleClick = (data, index) => {
    setActiveIndex(index);
  };

  // Handle filter change
  const handleBarClick = (dataKey) => {
    setActiveBar(activeBar === dataKey ? 'all' : dataKey);
  };

  // Custom tooltip to show exact values
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-lg rounded-md">
          <p className="font-bold text-gray-700 mb-1">{`${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm flex items-center my-1">
              <span className="w-3 h-3 mr-2 rounded-full" style={{ backgroundColor: entry.color }}></span>
              {`${entry.name}: $${entry.value.toLocaleString()}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Determine if we should use vertical or horizontal summary stats based on screen size
  const isSmallScreen = windowWidth < 768;

  return (
    <div className="bg-white shadow-md p-4 rounded-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <h2 className="text-xl font-semibold">{t('FeeManagement')}</h2>
        <div className="text-sm font-medium text-gray-600 mt-1 sm:mt-0">
          {growthPercentage > 0 ? (
            <span className="text-green-600">↑ {growthPercentage}% {t('Growth')}</span>
          ) : growthPercentage < 0 ? (
            <span className="text-red-600">↓ {Math.abs(growthPercentage)}% {t('Decline')}</span>
          ) : (
            <span className="text-gray-600">{t('NoChange')}</span>
          )}
        </div>
      </div>
      
      <div className="flex flex-wrap mb-4 gap-2">
        <button 
          onClick={() => handleBarClick('all')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('All')}
        </button>
        <button 
          onClick={() => handleBarClick('Collected')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'Collected' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('Collected')}: ${totalCollected.toLocaleString()}
        </button>
        <button 
          onClick={() => handleBarClick('Pending')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'Pending' ? 'bg-gradient-to-t from-[#241763] via-[#3c2a8a] to-[#5b4b9a] text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('Pending')}: ${totalPending.toLocaleString()}
        </button>
        <button 
          onClick={() => handleBarClick('Overdue')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'Overdue' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('Overdue')}: ${totalOverdue.toLocaleString()}
        </button>
        <button 
          onClick={() => handleBarClick('Late')}
          className={`px-3 py-1 rounded-full text-xs font-medium ${activeBar === 'Late' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'}`}
        >
          {t('Late')}: ${totalLate.toLocaleString()}
        </button>
      </div>
      
      {transformedData.length > 0 ? (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart 
            data={transformedData}
            margin={{ 
              top: 20, 
              right: isSmallScreen ? 10 : 30, 
              left: isSmallScreen ? 0 : 20, 
              bottom: isSmallScreen ? 60 : 5 
            }}
            barGap={8}
            barSize={20}
            onClick={handleClick}
          >
            <defs>
              <linearGradient id="pendingGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#5b4b9a" />
                <stop offset="50%" stopColor="#3c2a8a" />
                <stop offset="100%" stopColor="#241763" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: isSmallScreen ? 10 : 12 }}
              angle={isSmallScreen ? -45 : 0}
              textAnchor={isSmallScreen ? "end" : "middle"}
              height={isSmallScreen ? 60 : 30}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: isSmallScreen ? 10 : 12 }}
              tickFormatter={(value) => `$${value.toLocaleString()}`}
              width={isSmallScreen ? 50 : 60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ paddingTop: 10 }}
            />
            {(activeBar === 'all' || activeBar === 'Collected') && (
              <Bar 
                dataKey="Collected" 
                name={t('Collected')}
                fill="#10b981" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              >
                {!isSmallScreen && activeIndex !== null && 
                  <LabelList dataKey="Collected" position="top" formatter={(value) => `$${value}`} />
                }
              </Bar>
            )}
            {(activeBar === 'all' || activeBar === 'Pending') && (
              <Bar 
                dataKey="Pending" 
                name={t('Pending')}
                fill="url(#pendingGradient)" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              />
            )}
            {(activeBar === 'all' || activeBar === 'Overdue') && (
              <Bar 
                dataKey="Overdue" 
                name={t('Overdue')}
                fill="#f59e0b" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              />
            )}
            {(activeBar === 'all' || activeBar === 'Late') && (
              <Bar 
                dataKey="Late" 
                name={t('Late')}
                fill="#ef4444" 
                radius={[4, 4, 0, 0]}
                animationDuration={1500}
                isAnimationActive={true}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center text-gray-500">
          {t('NoFinancialData')}
        </div>
      )}
    </div>
  );
};

export default FinancialChart;